// app/ds/components/CanvasMinimap.js
'use client';

import React, { useEffect, useRef, useCallback } from 'react';
import styles from './CanvasMinimap.module.css';

/**
 * CanvasMinimap - A component that renders a miniature version of a target canvas.
 * Handles viewport indication and click-to-scroll navigation.
 */
export default function CanvasMinimap({
    canvasId = 'sprayCanvas',
    containerId = 'canvas-container',
    minimapWidth = 270,
    minimapHeight = 108,
    readOnly = false,
    className = '',
    style = {},
    showGrid = true,
    zoom = 1,
    title = ''
}) {
    const minimapRef = useRef(null);
    const rafRef = useRef(null);
    const isDragging = useRef(false);

    const navigateTo = useCallback((clientX, clientY) => {
        const minimap = minimapRef.current;
        const sourceCanvas = document.getElementById(canvasId);
        const container = document.getElementById(containerId);
        if (!minimap || !sourceCanvas || !container) return;

        const rect = minimap.getBoundingClientRect();
        const relX = (clientX - rect.left) / rect.width;
        const relY = (clientY - rect.top) / rect.height;

        // Center viewport on the selected point (considering CSS zoom of the canvas wrapper)
        const targetScrollX = (relX * sourceCanvas.width * zoom) - container.clientWidth / 2;
        const targetScrollY = (relY * sourceCanvas.height * zoom) - container.clientHeight / 2;

        container.scrollTo({
            left: Math.max(0, targetScrollX),
            top: Math.max(0, targetScrollY),
            behavior: 'smooth',
        });
    }, [canvasId, containerId, zoom]);

    useEffect(() => {
        const minimap = minimapRef.current;
        if (!minimap) return;

        const ctx = minimap.getContext('2d');

        const draw = () => {
            const sourceCanvas = document.getElementById(canvasId);
            const container = document.getElementById(containerId);

            if (!sourceCanvas || !container) {
                rafRef.current = requestAnimationFrame(draw);
                return;
            }

            const sw = sourceCanvas.width;
            const sh = sourceCanvas.height;
            const mw = minimapWidth;
            const mh = minimapHeight;

            const scaleX = mw / sw;
            const scaleY = mh / sh;
            const scale = Math.min(scaleX, scaleY);

            const drawW = sw * scale;
            const drawH = sh * scale;
            const offsetX = (mw - drawW) / 2;
            const offsetY = (mh - drawH) / 2;

            ctx.clearRect(0, 0, mw, mh);
            ctx.drawImage(sourceCanvas, 0, 0, sw, sh, offsetX, offsetY, drawW, drawH);

            if (showGrid) {
                const cellSize = 800 * scale; // 1m = 800px
                ctx.fillStyle = 'rgba(174, 174, 174, 0.4)';
                const dotSize = 2;
                for (let x = cellSize; x < drawW; x += cellSize) {
                    for (let y = cellSize; y < drawH; y += cellSize) {
                        ctx.fillRect(offsetX + x - dotSize / 2, offsetY + y - dotSize / 2, dotSize, dotSize);
                    }
                }
            }

            const vx = container.scrollLeft / zoom;
            const vy = container.scrollTop / zoom;
            const vw = container.clientWidth / zoom;
            const vh = container.clientHeight / zoom;

            const rx = offsetX + vx * scale;
            const ry = offsetY + vy * scale;
            const rw = Math.min(vw * scale, drawW - (vx * scale));
            const rh = Math.min(vh * scale, drawH - (vy * scale));

            const style = getComputedStyle(document.documentElement);
            const fillColor = style.getPropertyValue('--minimap-viewport-fill').trim() || 'rgba(255, 255, 255, 0.15)';
            const borderColor = style.getPropertyValue('--minimap-viewport-border').trim() || 'rgba(255, 255, 255, 0.3)';

            ctx.fillStyle = fillColor;
            ctx.beginPath();
            if (ctx.roundRect) {
                ctx.roundRect(rx, ry, rw, rh, 4);
            } else {
                ctx.rect(rx, ry, rw, rh);
            }
            ctx.fill();

            // Viewport border
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = 1;
            ctx.stroke();

            rafRef.current = requestAnimationFrame(draw);
        };

        rafRef.current = requestAnimationFrame(draw);

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [canvasId, containerId, minimapWidth, minimapHeight, showGrid, zoom]);

    const handleMouseDown = useCallback((e) => {
        if (readOnly) return;
        isDragging.current = true;
        navigateTo(e.clientX, e.clientY);
    }, [navigateTo, readOnly]);

    const handleMouseMove = useCallback((e) => {
        if (readOnly || !isDragging.current) return;
        navigateTo(e.clientX, e.clientY);
    }, [navigateTo, readOnly]);

    const handleMouseUp = useCallback(() => {
        if (readOnly) return;
        isDragging.current = false;
    }, [readOnly]);

    return (
        <div 
            className={`${styles.wrapper} ${className}`} 
            style={{ 
                ...style, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: minimapWidth,
                height: minimapHeight
            }}
            title={title}
        >
            <canvas
                ref={minimapRef}
                width={minimapWidth}
                height={minimapHeight}
                className={styles.canvas}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            />
        </div>
    );
}
